"use client";

import { useEffect, useState, type MouseEvent } from "react";
import { ChevronDownIcon } from "@/components/feature-icons";

type NavItem = { label: string; id: string };
type NavCategory = { id: string; label: string; items: NavItem[] };

export const FEATURE_NAV: NavCategory[] = [
  {
    id: "person-intelligence",
    label: "Person Intelligence",
    items: [
      { label: "12-point view", id: "person-intelligence" },
      { label: "Go deeper", id: "go-deeper" },
    ],
  },
  {
    id: "core-intelligence",
    label: "Core Intelligence",
    items: [
      { label: "People Intelligence", id: "people-intelligence" },
      { label: "Address Intelligence", id: "address-intelligence" },
      { label: "Connections", id: "connections" },
      { label: "Company Intelligence", id: "company-intelligence" },
      { label: "Company Credit Checks", id: "company-credit-checks" },
      { label: "Property Intelligence", id: "property-intelligence" },
      { label: "Contact & Record Status Intelligence", id: "contact-record-status" },
    ],
  },
  {
    id: "workflows",
    label: "Workflows",
    items: [
      { label: "Tracing", id: "tracing" },
      { label: "Background Research", id: "background-research" },
      { label: "Debt Recovery Intelligence", id: "debt-recovery" },
      { label: "Enforcement Intelligence", id: "enforcement" },
      { label: "KYC & Identity Verification", id: "kyc-identity-verification" },
      { label: "Screening & Review", id: "screening-review" },
    ],
  },
  {
    id: "data-operations",
    label: "Data Operations",
    items: [
      { label: "Data Cleansing & Enrichment", id: "data-cleansing" },
      { label: "Bulk Processing", id: "bulk-processing" },
      { label: "Auditable Search History", id: "auditable-search-history" },
      { label: "Geographic Intelligence", id: "geographic-intelligence" },
      { label: "Open-Source Research", id: "open-source-research" },
    ],
  },
  {
    id: "integrations",
    label: "API & Integrations",
    items: [
      { label: "Consolidated Person Intelligence", id: "api-access" },
      { label: "RESTful API", id: "api-rest" },
      { label: "CRM & Casework", id: "crm-casework" },
      { label: "Modular Integration", id: "modular-integration" },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    items: [
      { label: "Platform benefits", id: "platform" },
      { label: "Data with context", id: "data-with-context" },
    ],
  },
];

const CATEGORY_IDS = FEATURE_NAV.map((c) => c.id);
const CATEGORY_SET = new Set(CATEGORY_IDS);
const PARENT_OF: Record<string, string> = {};
FEATURE_NAV.forEach((c) => {
  c.items.forEach((item) => {
    PARENT_OF[item.id] = c.id;
  });
});

export function FeatureNavigator() {
  const [active, setActive] = useState<string>(CATEGORY_IDS[0]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const els = CATEGORY_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (els.length === 0) return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // The section furthest down the page that still intersects the reading band.
        for (let i = CATEGORY_IDS.length - 1; i >= 0; i -= 1) {
          if (visible.has(CATEGORY_IDS[i])) {
            setActive(CATEGORY_IDS[i]);
            break;
          }
        }
      },
      { rootMargin: "-140px 0px -55% 0px", threshold: [0, 1] },
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const activeLabel =
    FEATURE_NAV.find((c) => c.id === active)?.label ?? "Explore Features";

  function handleClick(event: MouseEvent<HTMLAnchorElement>, id: string) {
    event.preventDefault();
    setOpen(false);
    const category = CATEGORY_SET.has(id) ? id : PARENT_OF[id];
    if (category) setActive(category);
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    }
  }

  return (
    <div className="sticky top-20 z-40 border-b border-white/10 bg-brand-navy/95 backdrop-blur-md">
      <div className="mx-auto max-w-[1440px] px-container-padding">
        {/* Desktop: horizontal category bar */}
        <nav
          aria-label="Feature categories"
          className="hidden h-12 items-center md:flex"
        >
          <span className="mr-4 shrink-0 text-label-sm font-semibold uppercase tracking-[0.15em] text-white/40">
            Features
          </span>
          <ul className="flex items-center gap-1">
            {FEATURE_NAV.map((cat) => (
              <li key={cat.id}>
                <a
                  href={`#${cat.id}`}
                  onClick={(e) => handleClick(e, cat.id)}
                  aria-current={active === cat.id ? "true" : undefined}
                  className={`relative block px-3 py-1.5 text-sm transition-colors ${
                    active === cat.id
                      ? "font-semibold text-white after:absolute after:inset-x-3 after:-bottom-2.5 after:h-0.5 after:rounded-full after:bg-brand-cyan"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {cat.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile: single "Explore Features" control + dropdown */}
        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex h-12 w-full items-center justify-between gap-3 text-sm text-white"
          >
            <span className="min-w-0 truncate text-left">
              <span className="text-white/50">Explore Features · </span>
              <span className="font-semibold">{activeLabel}</span>
            </span>
            <ChevronDownIcon
              className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>

          {open && (
            <div className="max-h-[70vh] overflow-y-auto border-t border-white/10 pb-4 pt-2">
              {FEATURE_NAV.map((cat) => (
                <div key={cat.id} className="py-2">
                  <a
                    href={`#${cat.id}`}
                    onClick={(e) => handleClick(e, cat.id)}
                    className="block py-1 text-label-sm font-semibold uppercase tracking-wider text-brand-cyan"
                  >
                    {cat.label}
                  </a>
                  <ul className="mt-1 border-l border-white/10">
                    {cat.items.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleClick(e, item.id)}
                          className="block py-2 pl-4 text-sm text-white/70 hover:text-white"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
